import { useState, useEffect } from 'react';
import { 
  AppBar, 
  Box, 
  Drawer, 
  IconButton, 
  Toolbar, 
  Typography, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Link,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Badge
} from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  QrCode as QrCodeIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  Group as ClientesIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 260;

export const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('');
  const { signOut, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determinar la ruta activa para resaltar el elemento del menú
  useEffect(() => {
    const path = location.pathname.split('/').pop() || 'dashboard';
    setActiveItem(path);
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Función para generar iniciales del nombre de usuario
  const getUserInitials = () => {
    if (!userData?.name) return '?';
    return userData.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Función para navegar y cerrar el drawer en móvil
  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      id: 'dashboard'
    },
    {
      text: 'Mi QR',
      icon: <QrCodeIcon />,
      path: '/dashboard/qr',
      id: 'qr'
    },
    {
      text: 'Clientes',
      icon: <ClientesIcon />,
      path: '/dashboard/clientes',
      id: 'clientes'
    }
  ];

  // Añadir opción de gestión de usuarios si el rol es admin o consultor
  if (userData?.role === 'admin' || userData?.role === 'consultor') {
    menuItems.push({
      text: 'Gestión de Usuarios',
      icon: <PeopleIcon />,
      path: '/dashboard/users',
      id: 'users'
    });
  }

  const drawer = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      background: 'linear-gradient(135deg, #303f9f 0%, #1a237e 100%)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='500' height='500' viewBox='0 0 500 500' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='250' cy='250' r='240' fill='none' stroke='rgba(255, 255, 255, 0.35)' stroke-width='3'/%3E%3Cellipse cx='250' cy='250' rx='240' ry='60' fill='none' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cellipse cx='250' cy='250' rx='240' ry='120' fill='none' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cellipse cx='250' cy='250' rx='240' ry='180' fill='none' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cline x1='250' y1='10' x2='250' y2='490' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cline x1='10' y1='250' x2='490' y2='250' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cline x1='70' y1='70' x2='430' y2='430' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cline x1='430' y1='70' x2='70' y2='430' stroke='rgba(255, 255, 255, 0.3)' stroke-width='2'/%3E%3Cpath d='M200,150 Q230,140 250,160 Q270,180 300,170 Q320,160 330,180 Q340,200 320,220 Q300,240 310,260 Q320,280 300,290 Q280,300 260,280 Q240,260 220,270 Q200,280 190,260 Q180,240 190,220 Q200,200 180,180 Q160,160 200,150' fill='rgba(255, 255, 255, 0.15)' stroke='rgba(255, 255, 255, 0.35)' stroke-width='2'/%3E%3Cpath d='M150,300 Q170,290 190,310 Q210,330 230,320 Q250,310 260,330 Q270,350 250,360 Q230,370 210,350 Q190,330 170,340 Q150,350 140,330 Q130,310 150,300' fill='rgba(255, 255, 255, 0.15)' stroke='rgba(255, 255, 255, 0.35)' stroke-width='2'/%3E%3Cpath d='M320,280 Q340,270 360,290 Q380,310 400,300 Q420,290 430,310 Q440,330 420,340 Q400,350 380,330 Q360,310 340,320 Q320,330 310,310 Q300,290 320,280' fill='rgba(255, 255, 255, 0.15)' stroke='rgba(255, 255, 255, 0.35)' stroke-width='2'/%3E%3C/svg%3E")`,
        backgroundSize: '130% 130%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        opacity: 0.4,
        zIndex: 0,
        pointerEvents: 'none'
      }
    }}>
      <Box sx={{ 
        py: 3, 
        px: 2, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Fade in={true} timeout={1000}>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontWeight: 700,
              fontFamily: '"Poppins", sans-serif',
              letterSpacing: '.1rem',
              background: 'linear-gradient(45deg, #FFF 30%, #E3F2FD 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}
          >
            ImmunoWorld
          </Typography>
        </Fade>
        {isMobile && (
          <IconButton 
            onClick={handleDrawerToggle}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      
      <Box sx={{ 
        px: 2, 
        py: 2, 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        borderRadius: 2,
        mx: 2,
        mb: 2
      }}>
        <Avatar 
          sx={{ 
            bgcolor: theme.palette.secondary.main,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            width: 40,
            height: 40
          }}
        >
          {getUserInitials()}
        </Avatar>
        <Box sx={{ ml: 2 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: 'white',
              fontSize: '0.9rem',
              lineHeight: 1.2
            }}
          >
            {userData?.name}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.75rem',
              textTransform: 'capitalize'
            }}
          >
            {userData?.role || 'Usuario'}
          </Typography>
        </Box>
      </Box>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', mx: 2 }} />
      
      <List sx={{ px: 1, py: 2, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <Zoom 
            key={item.id} 
            in={true} 
            style={{ 
              transitionDelay: `${menuItems.indexOf(item) * 100}ms` 
            }}
          >
            <ListItem 
              onClick={() => handleNavigation(item.path)} 
              sx={{ 
                cursor: 'pointer',
                borderRadius: 2,
                my: 0.5,
                px: 2,
                py: 1,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                ...(activeItem === item.id && {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 4,
                    height: '70%',
                    backgroundColor: theme.palette.secondary.main,
                    borderRadius: '0 4px 4px 0'
                  }
                }),
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(5px)',
                },
              }}
            >
              <ListItemIcon sx={{ 
                color: activeItem === item.id ? theme.palette.secondary.light : 'rgba(255, 255, 255, 0.7)',
                minWidth: 40,
                transition: 'all 0.3s ease'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: activeItem === item.id ? 600 : 400,
                    transition: 'all 0.3s ease',
                    fontSize: '0.95rem',
                    color: 'white' // Añadir color blanco para mejor contraste
                  } 
                }}
              />
            </ListItem>
          </Zoom>
        ))}
      </List>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', mx: 2 }} />
      
      <List sx={{ px: 1, py: 1, mb: 2 }}>
        <ListItem 
          onClick={handleLogout} 
          sx={{ 
            cursor: 'pointer',
            borderRadius: 2,
            my: 0.5,
            px: 2,
            py: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateX(5px)',
            },
            color: theme.palette.error.light,
            '& .MuiListItemIcon-root': {
              color: theme.palette.error.light,
              minWidth: 40
            }
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Cerrar Sesión" 
            primaryTypographyProps={{ 
              sx: { 
                fontWeight: 500,
                fontSize: '0.95rem',
                color: 'white' // Añadir color blanco para mejor contraste
              } 
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 600,
                display: { xs: 'none', sm: 'block' },
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              {activeItem === 'dashboard' && 'Dashboard'}
              {activeItem === 'qr' && 'Mi QR'}
              {activeItem === 'clientes' && 'Clientes'}
              {activeItem === 'users' && 'Gestión de Usuarios'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Notificaciones">
              <IconButton 
                color="inherit" 
                sx={{ 
                  mx: 1,
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    color: theme.palette.primary.main
                  }
                }}
              >
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Configuración">
              <IconButton 
                color="inherit" 
                sx={{ 
                  mx: 1,
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    color: theme.palette.primary.main
                  }
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            
            <Button 
              variant="outlined" 
              color="error" 
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{
                ml: 2,
                display: { xs: 'none', md: 'flex' },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontSize: '0.8rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }
              }}
            >
              Salir
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: 'all 0.3s ease',
        }}
        className="fade-in"
      >
        <Box className="slide-up" sx={{ animation: 'slideUp 0.5s ease' }}>
          <Outlet />
        </Box>
        
        <Box 
          sx={{ 
            mt: 6, 
            pt: 2, 
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
            color: 'text.secondary',
            opacity: 0.8
          }}
        >
<Typography variant="body2">
  Desarrollado por{' '}
  <Link 
    href="https://www.ciudalia.com" 
    target="_blank" 
    rel="noopener noreferrer"
    sx={{ 
      color: 'primary.main',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: 'primary.dark',
        textDecoration: 'none'
      }
    }}
  >
    Ciudalia
  </Link>{' '}
  © para Arrigal 11, S.L. 2025 - {' '}
  <Link
    href="/aviso-legal"
    target="_blank"
    rel="noopener noreferrer"
    sx={{ 
      color: 'primary.main',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: 'primary.dark',
        textDecoration: 'none'
      }
    }}
  >
    Aviso Legal y Política de privacidad
  </Link>
</Typography>
        </Box>
      </Box>
    </Box>
  );
};
